import type { WorkflowDefinition } from "../types";

/**
 * W20 — See which bots read the site and what they take.
 *
 * The reporting half of the pair: this one answers "who is reading us", and
 * W18 is what you do about the answer. `shared` and `observed`, because the
 * plugin and the traffic behind it are standing facts about the property.
 *
 * Three of its five steps are manual. Reading a category timeline, drilling
 * into a named agent and slicing by page are all acts of judgement, and the
 * closing step is optional because either follow-on is a legitimate end.
 */
export const botTrafficWorkflow: WorkflowDefinition = {
	id: "bot-traffic",
	wNumber: 20,
	title: "See which bots read your site and what they take",
	outcome:
		"Turn invisible server traffic into a clear picture of who crawls you and why.",
	audiences: ["inhouse"],
	uses: ["Analytics Setup", "Bot Traffic", "Traffic Control"],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	isApplicable: (mode) => mode.showAnalytics,
	articleSlug: "how-to-see-which-bots-are-crawling-your-site",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "install-plugin",
			title: "Count bots at the server",
			page: "Analytics Setup",
			route: "/analytics",
			criterion: "You've installed the plugin and traffic is arriving.",
			why: "Bots do not execute JavaScript, so a browser tag never sees them. The counting has to happen in the request path.",
			docsHref: "/docs/dashboards/ai-traffic-analytics",
			tasks: [
				"Install the plugin for your platform",
				"Give it a day before you read anything into the numbers",
			],
			complete: { kind: "flag", key: "isVerified" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Traffic arriving",
									done: state.property.isVerified,
								},
							],
						}
					: null,
		},
		{
			id: "category-timeline",
			title: "Read the split between the categories",
			page: "Bot Traffic",
			route: "/analytics-dashboard",
			criterion: "You've read the category timeline.",
			why: "Assistants, model trainers, search crawlers and scrapers are worth completely different things to you. One total hides all of it.",
			docsHref: "/docs/dashboards/bot-traffic",
			tasks: [
				"Read the timeline by category over the last month",
				"Note which category is growing fastest",
			],
			complete: { kind: "manual" },
		},
		{
			id: "single-bots",
			title: "Drill into the individual bots",
			page: "Bot Traffic",
			route: "/analytics-dashboard",
			criterion: "You've looked at the named bots.",
			why: "A category is an average of very different agents. The named bot is the thing you would actually allow or block.",
			docsHref: "/docs/dashboards/bot-traffic",
			tasks: [
				"Open a category and read the agents underneath it",
				"Check visits, unique pages and when each one was last seen",
				"Open the per-visit log for anything that looks odd",
			],
			complete: { kind: "manual" },
		},
		{
			id: "by-page-and-intent",
			title: "See which pages each bot takes",
			page: "Bot Traffic",
			route: "/analytics-dashboard",
			criterion: "You've sliced the traffic by page and intent.",
			why: "What a crawler keeps coming back for tells you what it values. That is usually not the page you would have guessed.",
			docsHref: "/docs/dashboards/bot-traffic",
			tasks: [
				"Slice the traffic by page, intent and location",
				"Check whether your commercial pages are being read at all",
			],
			complete: { kind: "manual" },
		},
		{
			id: "decide-next",
			title: "Decide what to do with what you found",
			page: "Traffic Control",
			route: "/traffic-control",
			criterion: "You've set a rule or connected Google Analytics.",
			why: "The picture is only worth having if something follows it. Either set rules on who may read the site, or put the numbers in your reporting.",
			docsHref: "/docs/dashboards/traffic-control",
			tasks: [
				"Set rules for the crawlers you do not want",
				"Or forward AI visits into Google Analytics",
				"Come back monthly to check nothing has changed",
			],
			// Optional because both follow-ons are legitimate, and so is
			// deciding the picture alone was what you came for.
			optional: true,
			complete: {
				kind: "any",
				of: [
					{
						kind: "flag",
						key: "trafficControl.blockAiModelTrainers",
					},
					{
						kind: "count",
						key: "trafficControl.customBlockCount",
						gte: 1,
					},
					{
						kind: "analyticsPlatform",
						platform: "google-analytics",
					},
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Rules set",
									done:
										state.property.trafficControl
											.blockAiModelTrainers ||
										state.property.trafficControl
											.customBlockCount > 0,
								},
								{
									label: "Google Analytics connected",
									done: state.property.analyticsPlatforms.includes(
										"google-analytics",
									),
								},
							],
						}
					: null,
		},
	],
};
